import { credentialMarkup } from "../components";
import { appContent } from "../content";
import {
  bindJimListening,
  jimHeadMarkup,
  loginSuccessIdeaMs,
  loginSuccessThinkingMs,
  setJimMood,
  setJimVisual,
  startJimIdleLoop,
  stopJimIdleLoop
} from "../jim";
import type { CurrentUser, JimMood, SessionState } from "../types";
import { escapeHtml, randomBetween, wait } from "../utils";

type LoginPayload = {
  user: CurrentUser;
  csrfToken: string;
};

type LoginScreenOptions = {
  message: string;
  setupCredential: SessionState["setupCredential"];
  turnstileSiteKey: string | null;
  loginMood: () => JimMood;
  renderShell: (content: string, shellVariant: "login") => void;
  login: (username: string, password: string, turnstileToken?: string) => Promise<LoginPayload>;
  onLoginSuccess: (payload: LoginPayload) => void;
  onLoginFailure: (message: string) => void;
  beforeLoginAttempt: () => void;
  bindCopyButtons: () => void;
};

type Turnstile = {
  render: (container: HTMLElement, options: { sitekey: string; theme: "light"; size: "flexible" }) => string;
  getResponse: (widgetId: string) => string;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

const turnstileScriptUrl = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${turnstileScriptUrl}"]`);
    const script = existing || document.createElement("script");
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true });
    if (!existing) {
      script.src = turnstileScriptUrl;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    turnstileScriptPromise = null;
    document.querySelector<HTMLScriptElement>(`script[src="${turnstileScriptUrl}"]`)?.remove();
    throw error;
  });
  return turnstileScriptPromise;
}

function setupCredentialMarkup(credential: SessionState["setupCredential"]): string {
  return credentialMarkup(credential, {
    heading: appContent.credentials.superAdminCreated,
    note: appContent.credentials.setupNote
  });
}

export function renderLoginScreen(options: LoginScreenOptions): void {
  const loginPrompt = options.message || appContent.login.defaultPrompt;
  const mood = options.loginMood();
  const turnstileMarkup = options.turnstileSiteKey
    ? '\n          <div class="turnstile-widget" id="turnstileWidget"></div>'
    : "";
  options.renderShell(`
    <section class="login-screen">
      <div class="login-mobile-intro">
        <div class="mobile-jim-frame">
          ${jimHeadMarkup(mood)}
        </div>
        <div>
          <h1>${appContent.login.mobileHeading}</h1>
          <p>${appContent.login.mobileBody}</p>
        </div>
      </div>
      <div class="login-panel">
        <div class="login-panel-heading">
          <span class="eyebrow">${appContent.login.eyebrow}</span>
          <h1>${appContent.login.heading}</h1>
          <p>${appContent.login.body}</p>
        </div>
        <form id="loginForm" class="stack">
          <label>${appContent.login.usernameLabel}
            <input name="username" autocomplete="username" required />
          </label>
          <label>${appContent.login.passwordLabel}
            <input name="password" type="password" autocomplete="current-password" required />
          </label>${turnstileMarkup}
          <button type="submit">${appContent.login.submit}</button>
        </form>
        ${setupCredentialMarkup(options.setupCredential)}
      </div>
      <div class="login-jim-stage login-guidance-panel" aria-live="polite">
        <div class="field-tip-heading">
          <div>
            <h2>${appContent.login.fieldAccessHeading}</h2>
          </div>
        </div>
        <div class="login-jim-content">
          <div class="login-speech-card ${options.message ? "login-speech-card--warning" : ""}">
            <span class="eyebrow">${appContent.login.jimSaysLabel}</span>
            <strong>${escapeHtml(loginPrompt)}</strong>
            ${options.message ? `<p>${appContent.login.warningSupport}</p>` : `
              <p class="login-support-line login-support-line--strong">${appContent.login.defaultSupportLead}</p>
              ${appContent.login.defaultSupportExamples.map((item) => `<p class="login-support-line login-support-line--example">${escapeHtml(item)}</p>`).join("")}
              <p class="login-support-line login-support-line--strong">${appContent.login.defaultSupportClose}</p>
            `}
          </div>
          <div class="login-avatar-frame">
            ${jimHeadMarkup(mood)}
          </div>
        </div>
      </div>
    </section>
  `, "login");

  let widgetId: string | null = null;
  if (options.turnstileSiteKey) {
    const sitekey = options.turnstileSiteKey;
    const container = document.querySelector<HTMLElement>("#turnstileWidget");
    if (container) {
      void loadTurnstile().then(() => {
        if (container.isConnected && window.turnstile) {
          widgetId = window.turnstile.render(container, { sitekey, theme: "light", size: "flexible" });
        }
      }).catch(() => undefined);
    }
  }

  document.querySelector<HTMLFormElement>("#loginForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    stopJimIdleLoop();
    options.beforeLoginAttempt();
    setJimMood("thinking");
    const formElement = event.currentTarget as HTMLFormElement;
    formElement.querySelector<HTMLButtonElement>("button[type='submit']")?.setAttribute("disabled", "true");
    const form = new FormData(formElement);
    try {
      const turnstileToken = widgetId && window.turnstile
        ? window.turnstile.getResponse(widgetId) || undefined
        : undefined;
      const payload = await options.login(
        String(form.get("username") || ""),
        String(form.get("password") || ""),
        turnstileToken
      );
      const speechTitle = document.querySelector<HTMLElement>(".login-speech-card strong");
      const speechBody = document.querySelector<HTMLElement>(".login-speech-card p");
      document.querySelector(".login-speech-card")?.classList.remove("login-speech-card--warning");
      if (speechTitle) speechTitle.textContent = appContent.login.successPrompt;
      if (speechBody) speechBody.textContent = appContent.login.successSupport;
      setJimVisual("thinkingGif", { restart: true });
      await wait(loginSuccessThinkingMs);
      setJimVisual("ideaGif", { restart: true });
      await wait(loginSuccessIdeaMs);
      options.onLoginSuccess(payload);
    } catch (error) {
      if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
      options.onLoginFailure(error instanceof Error ? error.message : appContent.login.genericFailure);
    }
  });

  bindJimListening(document, options.loginMood, () => !options.message);
  options.bindCopyButtons();
  if (!options.message) {
    startJimIdleLoop(randomBetween(1400, 2600));
  }
}
